import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { Bar } from "react-chartjs-2";

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";

import { getAttributeValue } from "../../utils/utils";

import axios from "../../api/axios";

import APRComment from "./APRComment.js/AprComments";

import APRmemo from "./APRComment.js/APRmemo";



ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);



const AAP_PROGRAM = "ArLnAxhykoz";

const AAP_YEAR_ATTRIBUTE = "pWkYqcKukAY";

const BASELINE_PROGRAM = "pcG18cDzLtf";

const BASELINE_YEAR_ATTRIBUTE = "Ik7fouv4KOA";

const AAP_STATUS_ELEMENT = "SZcHb5mvjJx";



const Table_2 = forwardRef(({ year, district, period, hideTableDis }, ref) => {

  const [tableData, setTableData] = useState([]);



  const selectedYear = useMemo(() => {

    const parsed = parseInt(year, 10);

    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();

  }, [year]);



  useEffect(() => {

    getBaselinesAndTargets();

  }, [selectedYear, district, period]);



  useImperativeHandle(ref, () => ({

    getData: () => ({

      indicator: "Table_2",

      tableData,

    }),

  }));



  async function getBaselinesAndTargets() {

    if (!district) {

      setTableData([]);

      return;

    }



    try {

      const [baselineCurrentResp, baselineOldResp] = await Promise.all([

        axios.get(

          `/tracker/trackedEntities?orgUnit=${district}&program=${BASELINE_PROGRAM}&filter=${BASELINE_YEAR_ATTRIBUTE}:EQ:${selectedYear}&pageSize=5000`,

        ),

        axios.get(

          `/tracker/trackedEntities?orgUnit=${district}&program=${BASELINE_PROGRAM}&filter=${BASELINE_YEAR_ATTRIBUTE}:EQ:${selectedYear - 3}&pageSize=5000`,

        ),

      ]);



      const currentYear = baselineCurrentResp.data.instances || [];

      const oldActivities = baselineOldResp.data.instances || [];



      if (currentYear.length === 0) {

        setTableData([]);

        return;

      }



      const years = [selectedYear - 2, selectedYear - 1, selectedYear];

      const actualByYear = {};



      for (const yr of years) {

        const [plansResp, eventsResp] = await Promise.all([

          axios.get(

            `/tracker/trackedEntities?orgUnit=${district}&program=${AAP_PROGRAM}&filter=${AAP_YEAR_ATTRIBUTE}:EQ:${yr}&pageSize=5000`,

          ),

          axios.get(

            `/tracker/events?program=${AAP_PROGRAM}&orgUnit=${district}&startDate=${yr}-01-01&endDate=${yr}-12-31&pageSize=10000`,

          ),

        ]);



        const plans = plansResp.data.instances || [];

        const reports = eventsResp.data.instances || [];

        actualByYear[yr] = formatData(plans, reports, yr);

      }



      const calculatedData = calculateActivityPercentages({

        actualFormated: actualByYear[selectedYear] || [],

        actual1Formated: actualByYear[selectedYear - 1] || [],

        actual2Formated: actualByYear[selectedYear - 2] || [],

      });



      let oldCompletedBaseline = 0;

      let oldOnGoingBaseline = 0;

      let oldAbandonedBaseline = 0;

      let oldYetToStartBaseline = 0;

      let oldMtdpBaseline = 0;



      oldActivities.forEach((item) => {

        oldCompletedBaseline = parseFloat(getAttributeValue("Proportion of Completed AAP Baseline", item)) || 0;

        oldOnGoingBaseline = parseFloat(getAttributeValue("Proportion of  On Going AAP Intervention Baseline", item)) || 0;

        oldAbandonedBaseline = parseFloat(getAttributeValue("Proportion of  Abandoned AAP Intervention Baseline", item)) || 0;

        oldYetToStartBaseline = parseFloat(getAttributeValue("Proportion of  Yet to Start AAP Intervention Baseline", item)) || 0;

        oldMtdpBaseline += parseFloat(getAttributeValue("Proportion of MTDP Implemented Baseline", item)) || 0;

      });



      const temps = [];



      currentYear.forEach((item) => {

        const completedTarget = parseFloat(getAttributeValue("Proportion of Completed AAP Target", item)) || 0;

        const onGoingTarget = parseFloat(getAttributeValue("Proportion of  On Going AAP Intervention Target", item)) || 0;

        const abandonedTarget = parseFloat(getAttributeValue("Proportion of  Abandoned AAP Intervention Target", item)) || 0;

        const yetToStartTarget = parseFloat(getAttributeValue("Proportion of  Yet to Start AAP Intervention Target", item)) || 0;

        const mtdpTarget = parseFloat(getAttributeValue("Proportion of MTDP Implemented Target", item)) || 0;

        const mtdpActual = parseFloat(getAttributeValue("Proportion of MTDP Implemented Actual", item)) || 0;



        const dataSet = [

          {

            oldCompletedBaseline,

            target: completedTarget,

            indicator: "Percentage of activities completed",

            actual2: 0,

            actual1: 0,

            actual: 0,

          },

          {

            oldOnGoingBaseline,

            target: onGoingTarget,

            indicator: "Percentage of on-going activities",

            actual2: 0,

            actual1: 0,

            actual: 0,

          },

          {

            oldAbandonedBaseline,

            target: abandonedTarget,

            indicator: "Percentage of activities abandoned",

            actual2: 0,

            actual1: 0,

            actual: 0,

          },

          {

            oldYetToStartBaseline,

            target: yetToStartTarget,

            indicator: "Percentage of activities yet to start",

            actual2: 0,

            actual1: 0,

            actual: 0,

          },

          {

            oldMtdpBaseline,

            target: mtdpTarget,

            actual: mtdpActual,

            indicator: "Proportion of the overall Medium-Term Development Plan implemented",

            actual2: 0,

            actual1: 0,

          },

        ];



        temps.push(dataSet);

      });



      const finalData = mergeCalculatedData({ temps, calculatedData }, selectedYear);

      const flattenData = finalData?.[0] || [];

      const finalFlap = renameBaselineKey(flattenData);



      setTableData(finalFlap);

    } catch (err) {

      console.error("Error in getBaselinesAndTargets:", err);

      setTableData([]);

    }

  }



  const formatData = (aap, reports, yr) => {

    const temp = [];



    aap.forEach((item) => {

      const currentReport = reports.filter((rep) => rep.trackedEntity === item.trackedEntity);

      let status = "";



      if (currentReport) {

        currentReport.forEach((curReport) => {

          curReport.dataValues.forEach((rep) => {

            if (rep.dataElement === AAP_STATUS_ELEMENT) {

              status = rep.value;

            }

          });

        });

      }



      temp.push({

        year: String(yr),

        developmentDimension: getAttributeValue("Development Dimension", item),

        status,

      });

    });



    return temp;

  };



  function mergeCalculatedData(data, reportYear) {

    const { temps, calculatedData } = data;



    const statusMap = {

      "Percentage of activities completed": "Completed",

      "Percentage of on-going activities": "Ongoing",

      "Percentage of activities abandoned": "Abandoned",

      "Percentage of activities yet to start": "Yet to be started",

    };



    return temps.map((group) =>

      group.map((item) => {

        const newItem = { ...item };

        const statusKey = statusMap[item.indicator];



        if (statusKey) {

          newItem.actual2 = parseFloat(calculatedData[String(reportYear - 2)]?.[statusKey] || 0);

          newItem.actual1 = parseFloat(calculatedData[String(reportYear - 1)]?.[statusKey] || 0);

          newItem.actual = parseFloat(calculatedData[String(reportYear)]?.[statusKey] || 0);

        }



        return newItem;

      }),

    );

  }



  function calculateActivityPercentages(data) {

    const statuses = ["Completed", "Yet to be started", "Ongoing", "Abandoned"];



    const allYears = [

      ...(data.actualFormated || []),

      ...(data.actual1Formated || []),

      ...(data.actual2Formated || []),

    ];



    const grouped = allYears.reduce((acc, { year: itemYear, status }) => {

      const key = String(itemYear);

      if (!acc[key]) acc[key] = [];

      acc[key].push(status?.trim() || "");

      return acc;

    }, {});



    const result = {};

    for (const [itemYear, statusList] of Object.entries(grouped)) {

      const total = statusList.length;

      const yearResult = {};



      statuses.forEach((status) => {

        const count = statusList.filter((st) => st === status).length;

        yearResult[status] = total > 0 ? ((count / total) * 100).toFixed(2) : "0.00";

      });



      const unspecifiedCount = statusList.filter((st) => !st).length;

      yearResult.Unspecified = total > 0 ? ((unspecifiedCount / total) * 100).toFixed(2) : "0.00";

      yearResult.Total = total;



      result[itemYear] = yearResult;

    }



    return result;

  }



  const renameBaselineKey = (data) =>

    data.map((item) => {

      const newItem = { ...item };

      for (const key in newItem) {

        if (key.endsWith("Baseline")) {

          newItem.baseline = newItem[key];

          delete newItem[key];

        }

      }

      return newItem;

    });



  const chartData = {

    labels: tableData.map((row) =>

      row.indicator.length > 25

        ? `${row.indicator.substring(0, 25)}...`

        : row.indicator,

    ),

    datasets: [

      {

        label: "Baseline (%)",

        data: tableData.map((row) => Number(row.baseline) || 0),

        backgroundColor: "rgba(54, 162, 235, 0.6)",

        borderColor: "rgba(54, 162, 235, 1)",

        borderWidth: 1,

      },

      {

        label: "Actual (%)",

        data: tableData.map((row) => Number(row.actual) || 0),

        backgroundColor: "rgba(255, 99, 132, 0.6)",

        borderColor: "rgba(255, 99, 132, 1)",

        borderWidth: 1,

      },

      {

        label: "Target (%)",

        data: tableData.map((row) => Number(row.target) || 0),

        backgroundColor: "rgba(75, 192, 192, 0.6)",

        borderColor: "rgba(75, 192, 192, 1)",

        borderWidth: 1,

      },

    ],

  };



  const chartOptions = {

    responsive: true,

    maintainAspectRatio: false,

    plugins: {

      legend: {

        position: "top",

      },

      title: {

        display: true,

        text: `Performance Analysis (${selectedYear})`,

      },

      tooltip: {

        callbacks: {

          label: (context) => `${context.dataset.label}: ${context.raw}%`,

        },

      },

    },

    scales: {

      y: {

        beginAtZero: true,

        max: 100,

        title: {

          display: true,

          text: "Percentage (%)",

        },

      },

      x: {

        title: {

          display: true,

          text: "Indicators",

        },

      },

    },

  };



  return (

    <div className="col-12">

      <div className="card">

        <div className="card-header">Table 2 – Proportion of the DMTDP Implemented </div>

        <div className="card-body">

          <APRmemo

            year={year}

            districtId={district}

            tableCommentedId={`table2-${year}`}

          />



          <div className="table-responsive">

            <table

              className="table table-bordered"

              style={{

                border: "1px solid #000",

                borderCollapse: "collapse",

                width: "100%",

                marginTop: "20px",

              }}

            >

              <thead style={{

                backgroundColor: "#d4edda",

                fontWeight: "bold",

              }}

              >

                <tr>

                  <th style={{ border: "1px solid #000" }}>Indicators</th>

                  <th style={{ border: "1px solid #000" }}>Baseline {selectedYear - 3}</th>

                  <th style={{ border: "1px solid #000" }}>Actual {selectedYear - 2}</th>

                  <th style={{ border: "1px solid #000" }}>Actual {selectedYear - 1}</th>

                  <th style={{ border: "1px solid #000" }}>Target {selectedYear}</th>

                  <th style={{ border: "1px solid #000" }}>Actual {selectedYear}</th>

                </tr>

              </thead>

              <tbody>

                {tableData.length > 0 ? (

                  tableData.map((row, index) => (

                    <tr key={index}>

                      <td style={{ border: "1px solid #000" }}>{row.indicator}</td>

                      <td style={{ border: "1px solid #000" }}>{row.baseline}</td>

                      <td style={{ border: "1px solid #000" }}>{row.actual2}</td>

                      <td style={{ border: "1px solid #000" }}>{row.actual1}</td>

                      <td style={{ border: "1px solid #000" }}>{row.target}</td>

                      <td style={{ border: "1px solid #000" }}>{row.actual}</td>

                    </tr>

                  ))

                ) : (

                  <tr>

                    <td colSpan="6" style={{ textAlign: "center" }}>

                      No data available for {selectedYear}

                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

          <p className="mt-2">

            <small>

              Source: {selectedYear - 3} - {selectedYear} MTDP, {selectedYear - 2}, {selectedYear - 1}, {selectedYear} AAPs & Progress Reports

            </small>

          </p>

          <hr />

          <h5>Comparison of Implementation Status: {selectedYear - 2} - {selectedYear}</h5>

          <div className="mt-4 apr-chart-wrap" style={{ minHeight: 400 }}>

            {tableData.length > 0 ? (

              <Bar data={chartData} options={chartOptions} />

            ) : (

              <p>Loading chart data...</p>

            )}

          </div>



          <APRComment

            data={tableData}

            year={year}

            districtId={district}

            tableCommentedId={`table2-${year}`}

            hideTableDis={hideTableDis}

          >

            {({ renderCommentInput, renderCommentList }) => (

              <>

                {renderCommentInput()}

                {renderCommentList()}

              </>

            )}

          </APRComment>

        </div>

      </div>

    </div>

  );

});



export default Table_2;


